import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Dashboard,
  getDashboard,
  getSummary,
  Summary,
  getAvailableOptionsForOrder,
  pauseLockTimer,
  Step
} from '|/app/services';
import AppFrameworkContext from '|/helpers/app-framework';
import { isNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';
import useEventHelper from '|/hooks/use-event-helper';
import useIsInBucket from '|/hooks/use-is-in-bucket';
import { NavigationState } from '|/types/navigation-state';
import { shouldBreakRedirectLoop } from './redirect-circuit-breaker';
import useControlShowEvents from './use-control-show-events';

interface DetectSkippedStepsParams {
  nextStepURL: string;
  prevStepURL: string;
  orderedSteps: Step[];
}

const detectSkippedSteps = ({
  nextStepURL,
  prevStepURL,
  orderedSteps
}: DetectSkippedStepsParams) => {
  if (nextStepURL === prevStepURL) {
    return;
  }

  const nextStep = orderedSteps.find(step => step.url === nextStepURL);
  const prevStep = orderedSteps.find(step => step.url === prevStepURL);

  if (!nextStep || !prevStep) {
    return;
  }

  if (nextStep.order > prevStep.order) {
    if (nextStep.order - prevStep.order === 1) {
      // only moved one step, so no skipped steps
      return;
    }

    const steps = orderedSteps.reduce<Record<string, boolean>>((acc, step) => {
      acc[step.url] = false;
      return acc;
    }, {});

    for (let i = prevStep.order + 1; i < nextStep.order; i++) {
      const skippedStep = orderedSteps.find(step => step.order === i);
      const url = skippedStep?.url ?? null;
      if (url !== null) {
        steps[url] = true;
      }
    }
    return steps;
  }
};

const useNextStepVm = (setDashboard: (_: Dashboard) => void, setSummary: (_: Summary) => void) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { previousPath = '' } = (location.state as NavigationState | null) ?? {};
  const { load } = useContext(AppFrameworkContext);
  const { sendSkipEvent } = useEventHelper();
  const isInBucket = useIsInBucket('pp-skip-poc', 'treatment');
  const fireControlShowEvents = useControlShowEvents();

  useEffect(() => {
    if (isInBucket === null) {
      return;
    }

    // Superseded runs must not reach the redirect below — the circuit breaker counts
    // each one, so a re-run would inflate the count without a real navigation.
    let cancelled = false;

    // Don't use AbortController here - these requests are needed by other components
    // even after this component unmounts
    const dashboardPromise = getDashboard();
    const summaryPromise = getSummary();
    const availableOptionsPromise = getAvailableOptionsForOrder();
    load(
      Promise.all([dashboardPromise, summaryPromise, availableOptionsPromise]).then(
        ([dashboard, summary, availableOptions]) => {
          if (cancelled) {
            return null;
          }
          if (dashboard === null || summary === null) {
            navigate('/vehicle/missing', { replace: true });
            return;
          }
          setDashboard(dashboard);
          setSummary(summary);
          if (
            availableOptions &&
            !availableOptions.needsToScheduleTimeSlot &&
            !availableOptions.isVehicleLockPaused
          ) {
            void pauseLockTimer();
          }
          const {
            vehicle: { isAvailable = false, id = null } = {},
            totals: { vehicleStickerPrice = 0 } = {}
          } = summary;
          if (isAvailable !== true) {
            navigate(`/vehicle/unavailable/${id}`, { replace: true });
            return;
          }
          if (vehicleStickerPrice < 6000) {
            navigate('/vehicle/error-price', { replace: true });
            return;
          }
          const { search } = location;
          const searchParams = new URLSearchParams(search);
          const returnUrl = searchParams.get('returnUrl');
          if (returnUrl !== null) {
            navigate(returnUrl, { replace: true });
            return;
          }
          const { steps = [] } = dashboard;
          if (steps.length === 0) {
            return;
          }
          const orderedSteps = steps.sort((a, b) => a.order - b.order);
          const activeStep = orderedSteps.find(step => step.isEnabled && !step.isComplete);

          const firstStepName = orderedSteps[0];

          if (activeStep === undefined && !firstStepName.isEnabled) {
            // step.description is customer-personalized free text — never send it to RUM.
            datadogRum.addError(new Error('disabled first step, no active step'), {
              customType: 'disabledFirstStep',
              steps: orderedSteps.map(({ id, order, isEnabled, isComplete }) => ({
                id,
                order,
                isEnabled,
                isComplete
              }))
            });
            navigate('/vehicle/purchase-error', { replace: true });
            return null;
          }

          const nextStep = activeStep?.url ?? firstStepName.url;

          const newPath = `/steps/${nextStep}`;
          if (previousPath.startsWith(newPath)) {
            // step.description is customer-personalized free text — never send it to RUM.
            datadogRum.addError(new Error('step loop detected'), {
              customType: 'stepLooping',
              stepUrl: newPath,
              previousStepUrl: previousPath,
              activeStep: activeStep && {
                id: activeStep.id,
                order: activeStep.order,
                isEnabled: activeStep.isEnabled,
                isComplete: activeStep.isComplete
              },
              firstStepName: {
                id: firstStepName.id,
                order: firstStepName.order,
                isEnabled: firstStepName.isEnabled,
                isComplete: firstStepName.isComplete
              }
            });
          } else if (previousPath !== '') {
            const [ignoredLead, ignoredStepsFolder, stepName, ...ignoredSubPages] =
              previousPath.split('/');

            if (isInBucket) {
              const skip = detectSkippedSteps({
                nextStepURL: nextStep,
                prevStepURL: stepName,
                orderedSteps
              });
              if (skip !== undefined) {
                sendSkipEvent({
                  steps: skip,
                  currentStep: nextStep,
                  previousStep: stepName
                });
              }
            }
            datadogRum.addAction('step_complete', {
              stepFullUrl: previousPath,
              stepName,
              nextStep
            });
          }

          if (isNativeWebview() && nextStep === 'order-placed') {
            sendNativeEvent({
              event: MobileWebviewEvents.NAVIGATE_TO_OPD,
              source: 'purchase'
            });
            return null;
          }

          const isInCheckItOutTreatment =
            summary.experiments?.some(
              e => e.key === 'pp-check-it-out' && e.bucket === 'treatment'
            ) ?? false;
          fireControlShowEvents(orderedSteps, isInCheckItOutTreatment);
          // Insurance renders on its own treatment-gated route, not inside /checkout.
          const destination =
            activeStep?.section === 2 && activeStep.id !== 'Insurance' && isInCheckItOutTreatment
              ? '/checkout'
              : `/steps/${nextStep}`;
          // Only a loop can reach the limit — the counter resets whenever the
          // destination changes, so normal progression never accumulates.
          const { tripped, count } = shouldBreakRedirectLoop(destination);
          if (tripped) {
            datadogRum.addError(new Error('redirect circuit breaker tripped'), {
              customType: 'redirectCircuitBreaker',
              redirectCount: count,
              blockedDestination: destination
            });
            navigate('/vehicle/purchase-error', { replace: true });
            return null;
          }
          navigate(destination, { replace: true });
          return null;
        }
      )
    ).catch(() => void 0);

    return () => {
      cancelled = true;
    };
  }, [
    load,
    navigate,
    location,
    previousPath,
    setDashboard,
    setSummary,
    sendSkipEvent,
    isInBucket,
    fireControlShowEvents
  ]);
};

export default useNextStepVm;
